import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalFooter,
  ButtonGroup,
  SaveButton,
  CancelButton,
  InputContainer,
  ErrorContainer
} from './RenameCardTypeCategoryModal.styles';
import InputBar from '../../../../../../components/input-bar/InputBar';
import CriticalMessage from '../../../../../../components/system/critical-message/CriticalMessage';

export default function RenameCardTypeCategoryModal({ 
  isOpen, 
  onClose,
  category,
  onRename
}) {
  // Initialize with category name if available
  const [categoryName, setCategoryName] = useState(category?.name || '');
  const [isRenaming, setIsRenaming] = useState(false);
  const [error, setError] = useState('');

  // Update category name when category changes or modal opens
  useEffect(() => {
    if (isOpen && category) {
      setCategoryName(category.name || '');
      setError('');
    } else if (!isOpen) {
      // Reset when modal closes
      setCategoryName('');
      setError('');
      setIsRenaming(false);
    }
  }, [category, isOpen]);

  const handleClose = () => {
    setCategoryName('');
    setError('');
    setIsRenaming(false);
    onClose();
  };

  const handleSave = async () => {
    if (!categoryName.trim()) {
      setError('Category name is required');
      return;
    }

    if (categoryName.trim() === category?.name) {
      // No change, just close
      handleClose();
      return;
    }

    setIsRenaming(true);
    setError('');

    try {
      // Call the callback with the new name
      if (onRename) {
        await onRename(category, categoryName.trim());
      }
      handleClose();
    } catch (err) {
      console.error('Error renaming category:', err);
      setError(err.message || 'Failed to rename category. Please try again.');
      setIsRenaming(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay onClick={handleClose} className='widget-bg'>
      <ModalContainer onClick={(e) => e.stopPropagation()} className='panel-bg'>
        <ModalHeader>
          <ModalTitle>Rename Category</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          <InputContainer>
            <InputBar
              type="text"
              label="Category Name"
              value={categoryName}
              updateValue={setCategoryName}
              id="rename-category-name-input"
            />
          </InputContainer>
          {error && (
            <ErrorContainer>
              <CriticalMessage text={error} color='red' />
            </ErrorContainer>
          )}
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <SaveButton 
              onClick={handleSave}
              disabled={!categoryName.trim() || isRenaming}
            >
              {isRenaming ? 'Saving...' : 'Save'}
            </SaveButton>
            <CancelButton onClick={handleClose} disabled={isRenaming}>
              Cancel
            </CancelButton>
          </ButtonGroup>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}

